import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  UserProfile,
  UserProfilePatch,
  UserLocationPreferences,
  UserLocationPreferencesPatch,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface ProfileResponse {
  profile: UserProfile | null;
}

export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: () => api<ProfileResponse>("/api/profile"),
    staleTime: 30_000,
  });
}

export interface ProfileError {
  status: number;
  error: string;
}

function toProfileError(err: unknown): ProfileError {
  if (err instanceof ApiError) {
    let payload: { error?: string } = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return { status: err.status, error: payload.error ?? "request_failed" };
  }
  return { status: 0, error: "network_error" };
}

export function usePatchProfile(
  successText: string | ((data: ProfileResponse) => string) = "Profile saved.",
) {
  const qc = useQueryClient();
  return useMutationToast<ProfileResponse, ProfileError, UserProfilePatch>({
    mutationFn: async (input) => {
      try {
        return await api<ProfileResponse>("/api/profile", {
          method: "PATCH",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toProfileError(err);
      }
    },
    onSuccess: (data) => {
      qc.setQueryData<ProfileResponse>(["profile"], data);
    },
    success: successText,
    errorPrefix: "Save failed",
  });
}

export interface LocationPrefsResponse {
  location_preferences: UserLocationPreferences | null;
}

export function useLocationPrefs() {
  return useQuery({
    queryKey: ["profile", "location"],
    queryFn: () => api<LocationPrefsResponse>("/api/profile/location"),
    staleTime: 30_000,
  });
}

export interface PatchLocationPrefsResponse {
  location_preferences: UserLocationPreferences;
  recompute: { scanned: number; updated: number };
}

export function usePatchLocationPrefs(
  successText: string | ((data: PatchLocationPrefsResponse) => string) =
    "Location preferences saved.",
) {
  const qc = useQueryClient();
  return useMutationToast<
    PatchLocationPrefsResponse,
    ProfileError,
    UserLocationPreferencesPatch
  >({
    mutationFn: async (input) => {
      try {
        return await api<PatchLocationPrefsResponse>("/api/profile/location", {
          method: "PATCH",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toProfileError(err);
      }
    },
    onSuccess: (data) => {
      qc.setQueryData<LocationPrefsResponse>(["profile", "location"], {
        location_preferences: data.location_preferences,
      });
      // /jobs ordering depends on location_fit + the prefsFingerprint cache
      // key. Drop the local cache so the next list view fetches the
      // recomposed payload.
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["shortlist"] });
    },
    success: successText,
    errorPrefix: "Save failed",
  });
}
